// SPDX-License-Identifier: FSL-1.1-Apache-2.0
// Copyright 2026 Amantus Machina GmbH

'use client';

import { useReportWebVitals } from 'next/web-vitals';
import { useMemo } from 'react';
import { z } from 'zod';
import { env } from '@/env';
import { LogLevel, useLogger } from '@/lib/better-stack-logger.client';
import { isProductionRuntime } from '@/lib/runtime-flags';
import { isAutomationRuntime } from '@/lib/runtime-mode';
import { getBrowserWindow } from '@/lib/utils/browser-window';

export function BetterStackWebVitals() {
  const shouldSendRemoteLogs = isProductionRuntime() || env.NEXT_PUBLIC_VERCEL_ENV === 'production';
  const loggerOptions = useMemo(() => (shouldSendRemoteLogs ? {} : { logLevel: LogLevel.off }), [shouldSendRemoteLogs]);
  const logger = useLogger(loggerOptions);
  const shouldReport = !isAutomationRuntime();

  const handleMetrics = (rawMetric: unknown) => {
    if (!shouldReport) {
      return;
    }

    if (!isReportableMetric(rawMetric)) {
      return;
    }

    const metric: ReportableMetric = rawMetric;
    const { name, value, rating, delta, id, navigationType } = metric;

    const isPoor = rating === 'poor';
    const isCriticalValue =
      (name === 'LCP' && value > 3500) ||
      (name === 'CLS' && value > 0.15) ||
      (name === 'FID' && value > 200) ||
      (name === 'TTFB' && value > 2500);
    const sampled = Math.random() < 0.1;

    const shouldLogMetric = isPoor || isCriticalValue || sampled;
    if (!shouldLogMetric) {
      return;
    }

    const browserWindow = getBrowserWindow();
    const pathname = browserWindow?.location?.pathname ?? null;
    const userAgent = browserWindow?.navigator?.userAgent ?? null;

    type WebVitalsPayload = {
      name: string;
      value: number;
      rating: ReportableMetric['rating'];
      delta: number;
      id: string;
      navigationType: ReportableMetric['navigationType'] | null;
      sampled: boolean;
      path: string | null;
      userAgent: string | null;
    };

    const payload = {
      name,
      value,
      rating,
      delta,
      id,
      navigationType: navigationType ?? null,
      sampled,
      path: pathname,
      userAgent,
    } satisfies WebVitalsPayload;

    logger.info('Web Vitals', payload);
  };

  useReportWebVitals((metric) => {
    handleMetrics(metric);
  });

  return null;
}

type ReportableMetric = {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType?: string;
};

const ReportableMetricSchema = z.object({
  name: z.string(),
  value: z.number(),
  rating: z.enum(['good', 'needs-improvement', 'poor']),
  delta: z.number(),
  id: z.string(),
  navigationType: z.string().optional(),
});

const isReportableMetric = (candidate: unknown): candidate is ReportableMetric => {
  const parsed = ReportableMetricSchema.safeParse(candidate);
  return parsed.success;
};
